/* Copyright (c) 2023 MOCA Systems, Inc. All rights reserved. */

import * as appStrings from '@touchplan/strings';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, Observable, Subject, combineLatest, of } from 'rxjs';
import { distinctUntilChanged, map, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
	Component,
	ChangeDetectionStrategy,
	inject,
	OnDestroy,
	OnInit,
	Input,
	TemplateRef,
	Output,
	EventEmitter,
} from '@angular/core';
import {
	ActiveLineStrategy,
	ImportType,
	Plan,
	Role,
	SwimlanePlacement,
	TicketImportFileUploadStatus,
	TicketImportSupportedFormats,
	ImportScope,
	TicketImportOperationType,
	ScheduleTemplateFileFormat,
} from '@touchplan/common';

import { MaterialModule } from 'src/app/common/material/material-module';
import { IExternalSystem } from 'src/app/services/external-systems.service';
import { BannerMessageHandler } from 'src/app/common/logging/banner-message-handler';
import { DialogReusableModule } from 'src/app/common/dialog-reusable/dialog-reusable.module';
import { SharedUIMaterialModule } from 'src/app/shared/ui/material/shared-ui-material.module';
import { PresentationModule } from 'src/app/common/presentation-components/presentation.module';
import {
	IUploadTicket,
	ImportTicketsStateService,
} from 'src/app/modules/import-tickets/services/import-tickets-state.service';
import { ImportTicketsService } from 'src/app/modules/import-tickets/services/import-tickets.service';
import { LearnMoreDialogService } from '../../services/learn-more-dialog.service';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import { DynamicFlagTypes } from '@touchplan/feature-flags';
import { analyticsEventsService } from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';
import {
	ImageCollectionComponent,
	ImageCollectionDialogResult,
} from '../image-collection.component/image-collection.component';
import { cssVar } from '@touchplan/css-var';
import { FileItem } from '../../services/image-collection.service';
import { UploadOutputFormat } from 'src/app/shared/ui/material/tp-file-upload.component/tp-file-upload.component';

export const ImportTypeList = new Map<string, ImportType>([
	[appStrings.IMPORT_TICKET_IMPORT_TYPE_CREATE_ONLY, ImportType.CREATE_UNIQUE_ESIDS],
	[appStrings.IMPORT_TICKET_IMPORT_TYPE_CREATE_DUPLICATES, ImportType.CREATE],
	[appStrings.IMPORT_TICKET_IMPORT_TYPE_UPDATE_ONLY, ImportType.UPDATE],
	[appStrings.IMPORT_TICKET_IMPORT_TYPE_CREATE_UPDATE, ImportType.CREATE_AND_UPDATE],
]);

export const SwimlanePlacementMap = new Map<string, SwimlanePlacement>([
	[appStrings.IMPORT_TICKET_SWIMLANE_PLACEMENT_NONE, SwimlanePlacement.NONE],
	[appStrings.IMPORT_TICKET_SWIMLANE_PLACEMENT_WBS, SwimlanePlacement.WBS],
	[appStrings.IMPORT_TICKET_SWIMLANE_PLACEMENT_LOCATION, SwimlanePlacement.LOCATION],
	[appStrings.IMPORT_TICKET_SWIMLANE_PLACEMENT_ROLE, SwimlanePlacement.ROLE],
]);

export const WBSSwimlaneLevelList = [1, 2, 3, 4];

export const ImportScopeList = new Map<string, ImportScope>([
	[appStrings.IMPORT_TICKET_SCOPE_TICKETS, ImportScope.TICKETS],
	[appStrings.IMPORT_TICKET_SCOPE_PREDECESSORS, ImportScope.PREDECESSORS],
]);

interface IDataSourceOption extends IExternalSystem {
	$id: string;
}

const DEFAULT_WBS_SWIMLANE_LEVEL = 4;

@Component({
	selector: 'import-tickets-upload-step',
	standalone: true,
	templateUrl: './import-tickets-upload-step.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	styleUrls: ['./import-tickets-upload-step.component.scss'],
	providers: [ImportTicketsService, BannerMessageHandler, LearnMoreDialogService],
	imports: [
		CommonModule,
		SharedUIMaterialModule,
		PresentationModule,
		ReactiveFormsModule,
		DialogReusableModule,
		MaterialModule,
		MatDialogModule,
	],
})
export class ImportTicketsUploadStepComponent implements OnInit, OnDestroy {
	@Input() planId: string;
	@Output() openDataSourceSettings = new EventEmitter<void>();

	public formBuilder = inject(FormBuilder);
	protected importTicketsState = inject(ImportTicketsStateService);
	private importTicketsService = inject(ImportTicketsService);
	private learnMoreDialogService = inject(LearnMoreDialogService);
	private featureFlagManager = inject(FeatureFlagManager);
	private dialog = inject(MatDialog);

	//can't really think of any concievable reason why this should stay observable...
	// also consider adding to the formGroup for centralized validation?
	private earlyUploadId?: string;
	private boardScanPlanStartDate?: string;
	protected boardScanFileItems?: FileItem[];

	public appStrings = appStrings;
	public UploadOutputFormat = UploadOutputFormat;
	public ticketImportSupportedFormats = TicketImportSupportedFormats;
	public ticketImportFileUploadStatus = TicketImportFileUploadStatus;
	public importTypeList$ = of(ImportTypeList);
	public swimlanePlacementList$ = of(SwimlanePlacementMap).pipe(
		// Filter out the WBS option if isBoardScanFlow is true
		switchMap((swimlanePlacementMap) =>
			this.isBoardScanImportSelected$.pipe(
				map((isBoardScanFlow) =>
					isBoardScanFlow
						? new Map(
								[...swimlanePlacementMap].filter(([, placement]) => placement !== SwimlanePlacement.WBS)
						  )
						: swimlanePlacementMap
				)
			)
		)
	);
	public wbsSwimlaneLevelList$ = of(WBSSwimlaneLevelList);
	public importScopeList$ = of(ImportScopeList);
	public dataSourceList$: Observable<Map<string, IDataSourceOption>> =
		this.importTicketsState.projectId$.pipe(
			switchMap((projectId) =>
				combineLatest([
					this.importTicketsState.dataSourceList,
					this.featureFlagManager.getFlag$(DynamicFlagTypes.TOUCHPLAN_FORMAT_ENABLED),
					this.featureFlagManager.getFlag$(DynamicFlagTypes.MSP_EXPORT_FORMAT_ENABLED, projectId),
					this.featureFlagManager.getFlag$(DynamicFlagTypes.BOARDSCAN_ENABLED, projectId),
				]).pipe(
					map(
						([dataSourceList, touchplanEnabled, mspEnabled, boardscanEnabled]) =>
							new Map(
								[...dataSourceList]
									.filter(
										([_, dataSource]) =>
											touchplanEnabled || dataSource.type !== ScheduleTemplateFileFormat.Touchplan
									)
									.filter(
										([_, dataSource]) => mspEnabled || dataSource.type !== ScheduleTemplateFileFormat.MSPExcel
									)
									.filter(
										([_, dataSource]) =>
											boardscanEnabled || dataSource.type !== ScheduleTemplateFileFormat.Boardscan
									)
									.map(([id, dataSource]) => [id, { ...dataSource, $id: id }])
							)
					)
				)
			)
		);

	private shutdownSubject$ = new Subject<void>();

	public formGroup = this.formBuilder.group({
		planId: this.formBuilder.control({ value: '', disabled: true }, [Validators.required]),
		importFile: this.formBuilder.control<File>(null, [Validators.required]),
		importScope: this.formBuilder.control(
			[ImportScope.TICKETS, ImportScope.PREDECESSORS],
			[Validators.required]
		),
		defaultRole: this.formBuilder.control('', [Validators.required]),
		importType: this.formBuilder.control<ImportType>(null, [Validators.required]),
		swimlanePlacement: this.formBuilder.control<SwimlanePlacement>(SwimlanePlacement.NONE),
		wbsLevel: this.formBuilder.control<number>({ value: null, disabled: true }, [Validators.required]),
		activeLineStrategy: this.formBuilder.control(false),
		preservePullTickets: this.formBuilder.control({ value: false, disabled: true }),
		dataSource: this.formBuilder.control<IDataSourceOption>(null, [Validators.required]),
		bsFiles: this.formBuilder.control<File[]>([]),
	});

	public selectedPlanWarningText$: Observable<string> = combineLatest([
		this.dataSourceFormControl.valueChanges,
		this.importTicketsState.selectedPlan$,
	]).pipe(
		map(([dataSourceSelected, plan]) => {
			if (plan && dataSourceSelected.type === ScheduleTemplateFileFormat.P6Excel) {
				const hasActiveLineDateAndX =
					!!plan.view.activeLineDate &&
					(plan.view.activeLineX !== null || plan.view.activeLineX !== undefined);
				return !hasActiveLineDateAndX
					? appStrings.IMPORT_TICKET_PLAN_DESTINATION_WARNING(plan.view.name)
					: '';
			}
		}),
		takeUntil(this.shutdownSubject$)
	);

	get planIdFormControl() {
		return this.formGroup.get('planId');
	}

	get importFileFormControl() {
		return this.formGroup.get('importFile');
	}

	get activeLineStrategyFormControl() {
		return this.formGroup.get('activeLineStrategy');
	}

	get preservePullTicketsFormControl() {
		return this.formGroup.get('preservePullTickets');
	}

	get dataSourceFormControl() {
		return this.formGroup.get('dataSource');
	}

	// Internal component state
	public ticketImportFileUploadStatus$: BehaviorSubject<TicketImportFileUploadStatus> = new BehaviorSubject(
		TicketImportFileUploadStatus.Waiting
	);

	readonly hasSelectedBoardScanFiles$ = this.formGroup.controls.bsFiles.valueChanges.pipe(
		map((files) => Array.isArray(files) && files.length > 0)
	);

	protected isBoardScanImportSelected$ = this.importTicketsState.projectId$.pipe(
		switchMap((projectId) =>
			combineLatest([
				this.formGroup.controls.dataSource.valueChanges,
				this.featureFlagManager.getFlag$(DynamicFlagTypes.BOARDSCAN_ENABLED, projectId),
			]).pipe(
				map(
					([dataSource, boardscanEnabled]) =>
						boardscanEnabled && dataSource?.type === ScheduleTemplateFileFormat.Boardscan
				)
			)
		),
		startWith(false) // Default to false until the value is determined
	);

	public onNext(): void {
		// This field might or might not appear in the screen, depending on some conditions.
		// For that, we need to mark it as dirty to ensure user feedback is provided.
		this.formGroup.controls.bsFiles.markAsDirty();
		const { dataSource } = this.formGroup.value;
		// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7046
		// can disable for boardscan mocking - remove this comment when done
		if (
			!this.formGroup.valid ||
			!this.planId ||
			(dataSource.type === ScheduleTemplateFileFormat.Boardscan && !this.earlyUploadId)
		) {
			return;
		}
		// END PLACEHOLDER REPLACE AS PART OF TP-7046
		const { importFile, importScope, defaultRole, importType, swimlanePlacement, wbsLevel } =
			this.formGroup.value;
		//some extra defense against the planId somehow changing in the ui
		//despite being disabled, defer to the input version
		//remove all reference of planId and undisable the formControl
		//to make the ui planId control work again
		this.importTicketsState.nextStep({
			importFile,
			importScope,
			defaultRole,
			importType,
			swimlanePlacement,
			wbsLevel,
			importFileFormat: dataSource.type,
			dataSourceId: dataSource.$id,
			activeLineStrategy: this.getActiveLineStrategy(),
			planId: this.planId,
			// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7046
			uploadId: this.earlyUploadId,
			// uploadId: 'steve',
			// END PLACEHOLDER REPLACE AS PART OF TP-7046
		} as IUploadTicket);
	}

	// BEGIN PLACEHOLDER REPLACE AS PART OF TP-5770
	public onBack(): void {
		this.importTicketsState.previousStep();
	}
	// END PLACEHOLDER REPLACE AS PART OF TP-5770

	public onDownloadTicketImportBlankSample(): void {
		// BEGIN PLACEHOLDER REPLACE AS PART OF TP-5770
		this.importTicketsService.downloadBlankSampleFile();
		// END PLACEHOLDER REPLACE AS PART OF TP-5770
		analyticsEventsService.ticketTemplateDownloaded(
			this.formGroup.controls.importType.value === ImportType.UPDATE ||
				this.formGroup.controls.importType.value === ImportType.CREATE_AND_UPDATE
				? TicketImportOperationType.update
				: TicketImportOperationType.import
		);
	}

	public onEditDataSource(): void {
		this.learnMoreDialogService.closeDialog();
		this.openDataSourceSettings.emit();
	}

	private loadRequiredData(): void {
		this.planIdFormControl.patchValue(this.planId, { emitEvent: false });
		const dataSourcesList$ = this.importTicketsState.projectId$.pipe(
			distinctUntilChanged(),
			switchMap((projectId) => this.importTicketsService.getDataSourcesList(projectId))
		);
		this.importTicketsState.updateDataSourcesList(dataSourcesList$);
	}

	private manageAutoDisabling() {
		//disable wbsLevel when swimlanePlacement is not wbs
		this.formGroup.controls.swimlanePlacement.valueChanges
			.pipe(
				map((val) => val === SwimlanePlacement.WBS),
				distinctUntilChanged(),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe((checked) => {
				const control = this.formGroup.controls.wbsLevel;
				if (checked) {
					control.enable();
					if (control.getRawValue() === null) {
						control.setValue(DEFAULT_WBS_SWIMLANE_LEVEL, { emitEvent: false });
					}
				} else {
					control.disable();
				}
			});

		//disable preserve pull tickets when advance active line is not true
		this.formGroup.controls.activeLineStrategy.valueChanges
			.pipe(distinctUntilChanged(), takeUntil(this.shutdownSubject$))
			.subscribe((checked) => {
				const control = this.formGroup.controls.preservePullTickets;
				checked ? control.enable() : control.disable();
			});

		//disable various fields when the user unchecks the Tickets option of the Import Scope multi select
		this.formGroup.controls.importScope.valueChanges
			.pipe(
				map((importScopes) => importScopes.includes(ImportScope.TICKETS)),
				distinctUntilChanged(),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe((isTicketsChecked) => {
				if (isTicketsChecked) {
					this.formGroup.controls.defaultRole.enable();
					this.formGroup.controls.importType.enable();
					this.formGroup.controls.swimlanePlacement.enable();
					this.formGroup.controls.activeLineStrategy.enable();
					this.formGroup.controls.preservePullTickets.enable();
				} else {
					this.formGroup.controls.defaultRole.disable();
					this.formGroup.controls.importType.disable();
					this.formGroup.controls.swimlanePlacement.disable();
					this.formGroup.controls.wbsLevel.disable();
					this.formGroup.controls.activeLineStrategy.disable();
					this.formGroup.controls.preservePullTickets.disable();
				}
			});

		this.isBoardScanImportSelected$
			.pipe(
				tap((isBoardScanFlow) => {
					const controlOptions = {
						emitEvent: false,
					};
					if (isBoardScanFlow) {
						this.formGroup.controls.importFile.disable(controlOptions);
						this.formGroup.controls.importScope.setValue([ImportScope.TICKETS], controlOptions);
						this.formGroup.controls.importScope.disable(controlOptions);
						this.formGroup.controls.defaultRole.disable(controlOptions);
						this.formGroup.controls.importType.setValue(ImportType.CREATE, controlOptions);
						this.formGroup.controls.importType.disable(controlOptions);

						this.formGroup.controls.bsFiles.setValidators([Validators.required]);
						this.formGroup.controls.bsFiles.updateValueAndValidity();
					} else {
						this.formGroup.controls.importFile.enable(controlOptions);
						this.formGroup.controls.importScope.enable(controlOptions);
						this.formGroup.controls.defaultRole.enable(controlOptions);
						this.formGroup.controls.importType.enable(controlOptions);

						this.formGroup.controls.bsFiles.setValidators([]);
						this.formGroup.controls.bsFiles.updateValueAndValidity();
					}
				}),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe();
	}

	private getActiveLineStrategy(): ActiveLineStrategy {
		if (!this.activeLineStrategyFormControl.value) {
			return ActiveLineStrategy.LEAVE_ACTIVE_LINE;
		} else if (this.preservePullTicketsFormControl.value) {
			return ActiveLineStrategy.PRESERVE_PULL_TICKETS;
		}
		return ActiveLineStrategy.ACTIVATE_PULL_TICKETS;
	}

	protected onLearnMoreClick(template: TemplateRef<any>): void {
		this.learnMoreDialogService.openDialog(template, { width: '615px' });
	}

	protected openBoardScanImageCollectionDialog() {
		this.importTicketsState.projectId$
			.pipe(
				take(1),
				switchMap((projectId) => {
					const dialogRef: MatDialogRef<ImageCollectionComponent, ImageCollectionDialogResult> =
						this.dialog.open(ImageCollectionComponent, {
							width: cssVar.dialogWidthLg,
							data: {
								projectId,
								ticketImportService: this.importTicketsService,
								jobId: this.earlyUploadId,
								fileItems: this.boardScanFileItems?.map((item) => ({ ...item })),
								planStartDate: this.boardScanPlanStartDate,
							},
						});
					return dialogRef.afterClosed().pipe(take(1));
				})
			)
			.subscribe((result) => {
				this.earlyUploadId = result?.jobId ?? this.earlyUploadId;
				this.boardScanPlanStartDate = result?.planStartDate ?? this.boardScanPlanStartDate;
				this.boardScanFileItems = result?.fileItems ?? this.boardScanFileItems;
				if (this.boardScanFileItems?.length > 0) {
					this.formGroup.controls.bsFiles.setValue(this.boardScanFileItems.map((item) => item.originalFile));
				} else {
					this.formGroup.controls.bsFiles.setValue([]);
				}
			});
	}

	private trackDataSourceChanges() {
		this.formGroup.controls.dataSource.valueChanges
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe((dataSource) => {
				analyticsEventsService.dataSourceEdited(this.planId, dataSource.$id);
			});
	}

	protected removeBoardScanItems(): void {
		this.earlyUploadId = undefined;
		this.boardScanFileItems = [];
		this.formGroup.controls.bsFiles.setValue([]);
	}

	public ngOnInit(): void {
		this.loadRequiredData();
		this.manageAutoDisabling();
		this.trackDataSourceChanges();
	}

	public ngOnDestroy(): void {
		this.ticketImportFileUploadStatus$.complete();
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
	}
}
